import {
  type DefectDetail,
  type DefectListResponse,
  REPAIR_STATUSES,
  REPAIR_STATUS_LABELS,
  defectDetailSchema,
  defectListResponseSchema,
} from '@sivan/shared';
import { useId, useState } from 'react';
import { ApiError, NetworkError, apiGet, apiPost } from '../api/client';
import { useCurrentProject } from '../current-project';
import { LoadingBlock, Problem } from '../handover/common';
import { useLoad, useWorkspace } from '../master-data/use-master-data';
import { navigate, useHash } from '../routing';
import { askConfirm, askText } from '../ui/confirm';
import { EmptyState } from '../ui/EmptyState';
import { Icon } from '../ui/Icon';
import { DefectCard, DefectStatus, PhotoStrip, RepairHistory, formatDate } from './parts';

type StatusFilter = (typeof REPAIR_STATUSES)[number] | 'overdue' | 'unreleased' | 'all';

const explain = (error: unknown): string => {
  if (error instanceof NetworkError) return 'אין חיבור לשרת. הפעולה לא בוצעה.';
  if (error instanceof ApiError) {
    const code = (error.body['conflict'] as { code?: string } | undefined)?.code;
    if (code === 'stale') return 'הליקוי השתנה בינתיים. הנתונים רועננו, נסו שוב.';
    if (code === 'repair_state') return 'מצב הליקוי השתנה ואינו מאפשר את הפעולה.';
    if (error.status === 422) return 'בדקו את הפרטים: קבלן, תאריך יעד והסבר (לפחות 3 תווים).';
    if (code === 'contractor_unknown') return 'הקבלן אינו משויך לפרויקט.';
    if (error.status === 403) return 'אין לך הרשאה לפעולה זו.';
  }
  return 'הפעולה נכשלה.';
};

/** ניהול ליקויים (DR-196): the current project's defects, for Sivan staff. */
export function DefectsScreen() {
  const [workspace] = useWorkspace();
  const projects = workspace.state === 'ready' ? workspace.data.projects : [];
  const project = useCurrentProject(projects);
  const parts = useHash().replace(/^#/, '').split('/');
  if (workspace.state === 'loading') return <LoadingBlock />;
  if (project === undefined) return <p className="empty">אין פרויקטים זמינים עבורך.</p>;
  const findingId = parts[1] === project.id ? (parts[2] ?? null) : null;
  return findingId === null ? (
    <DefectList key={project.id} projectId={project.id} />
  ) : (
    <DefectView key={findingId} projectId={project.id} findingId={findingId} />
  );
}

function DefectList({ projectId }: { readonly projectId: string }) {
  const id = useId();
  const [status, setStatus] = useState<StatusFilter>('all');
  const [building, setBuilding] = useState('');
  const [area, setArea] = useState('');
  const [trade, setTrade] = useState('');
  const [contractor, setContractor] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [text, setText] = useState('');
  const query = new URLSearchParams();
  if (status !== 'all') query.set('status', status);
  if (building !== '') query.set('building', building);
  if (area !== '') query.set('area', area);
  if (trade !== '') query.set('workCategory', trade);
  if (contractor !== '') query.set('contractorId', contractor);
  if (from !== '') query.set('from', from);
  if (to !== '') query.set('to', to);
  if (text.trim() !== '') query.set('q', text.trim());
  const qs = query.toString();
  const [loaded, reload] = useLoad(`defects:${projectId}:${qs}`, () =>
    apiGet(`/projects/${projectId}/defects${qs === '' ? '' : `?${qs}`}`, defectListResponseSchema),
  );
  const [last, setLast] = useState<DefectListResponse | null>(null);
  const data = loaded.state === 'ready' ? loaded.data : last;
  if (loaded.state === 'ready' && loaded.data !== last) setLast(loaded.data);
  if (loaded.state === 'forbidden')
    return (
      <EmptyState icon="lock" title="ניהול הליקויים אינו זמין לך">
        <p>הצגת הליקויים דורשת הרשאה בפרויקט.</p>
      </EmptyState>
    );
  if (loaded.state === 'error')
    return <Problem text="טעינת הליקויים נכשלה." requestId={loaded.requestId} onRetry={reload} />;
  if (data === null) return <LoadingBlock />;
  const tiles: [StatusFilter, string, number][] = [
    ['unreleased', 'טרם שוחררו', data.counts.unreleased],
    ['open', 'אצל הקבלן', data.counts.open],
    ['in_progress', 'בטיפול', data.counts.inProgress],
    ['fixed', 'ממתינים לבדיקה', data.counts.fixed],
    ['verified', 'אושרו ונסגרו', data.counts.verified],
    ['overdue', 'באיחור', data.counts.overdue],
  ];
  const filtered =
    status !== 'all' ||
    [building, area, trade, contractor, from, to, text.trim()].some((value) => value !== '');
  return (
    <div className="defects" aria-busy={loaded.state === 'loading'}>
      <p className="hint">
        ליקויים מופיעים כאן מיד כשהם נשמרים במסירה. נציג סיון משחרר ליקוי לקבלן; הקבלן מדווח על
        תיקון עם תמונה, ונציג סיון מאשר או מחזיר לטיפול.
      </p>
      <div className="defect-tiles" role="group" aria-label="מצב הליקויים">
        {tiles.map(([key, label, n]) => (
          <button
            key={key}
            type="button"
            className={`defect-tile defect-tile--${key}`}
            aria-pressed={status === key}
            onClick={() => {
              setStatus(status === key ? 'all' : key);
            }}
          >
            <b>{n}</b>
            <span>{label}</span>
          </button>
        ))}
      </div>
      <details className="defect-filters" open={filtered}>
        <summary>
          <Icon name="search" /> חיפוש וסינון
        </summary>
        <div className="defect-filters__grid">
          <label className="field">
            <span>חיפוש</span>
            <input
              type="search"
              value={text}
              placeholder="תיאור, רכיב, דירה או מספר פרוטוקול"
              onChange={(e) => {
                setText(e.target.value);
              }}
            />
          </label>
          <Select
            label="בניין"
            value={building}
            onChange={setBuilding}
            options={data.filters.buildings.map((b) => [b, b])}
          />
          <Select
            label="חדר"
            value={area}
            onChange={setArea}
            options={data.filters.areas.map((a) => [a, a])}
          />
          <Select
            label="תחום"
            value={trade}
            onChange={setTrade}
            options={data.filters.workCategories.map((w) => [w, w])}
          />
          <Select
            label="קבלן"
            value={contractor}
            onChange={setContractor}
            options={data.filters.contractors.map((c) => [c.id, c.name])}
          />
          <Select
            label="מצב"
            value={status === 'all' ? '' : status}
            onChange={(v) => {
              setStatus(v === '' ? 'all' : (v as StatusFilter));
            }}
            options={[
              ['unreleased', 'טרם שוחרר לקבלן'],
              ...REPAIR_STATUSES.map((s) => [s, REPAIR_STATUS_LABELS[s]] as [string, string]),
              ['overdue', 'באיחור'],
            ]}
          />
          <label className="field">
            <span>נבדק מתאריך</span>
            <input
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
              }}
            />
          </label>
          <label className="field">
            <span>עד תאריך</span>
            <input
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
              }}
            />
          </label>
        </div>
        {filtered && (
          <button
            type="button"
            className="button button--ghost"
            onClick={() => {
              setStatus('all');
              setBuilding('');
              setArea('');
              setTrade('');
              setContractor('');
              setFrom('');
              setTo('');
              setText('');
            }}
          >
            ניקוי הסינון
          </button>
        )}
      </details>
      <h2 className="defects__count" id={`${id}-list`}>
        {data.items.length === 1 ? 'ליקוי אחד' : `${String(data.items.length)} ליקויים`}
      </h2>
      {data.items.length === 0 ? (
        <EmptyState
          icon="circle-check"
          title={filtered ? 'אין ליקויים מתאימים' : 'אין ליקויים בפרויקט'}
        >
          <p>
            {filtered
              ? 'נסו לשנות את הסינון.'
              : 'ליקויים מופיעים כאן אחרי שמסירה עם ליקויים נסגרת.'}
          </p>
        </EmptyState>
      ) : (
        <ul className="defect-list" aria-labelledby={`${id}-list`}>
          {data.items.map((d) => (
            <DefectCard
              key={d.id}
              defect={d}
              href={`#defects/${projectId}/${d.id}`}
              action={d.status === 'fixed' && data.permissions.manage ? 'לבדיקה' : 'פרטים'}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly (readonly [string, string])[];
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <select
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      >
        <option value="">הכל</option>
        {options.map(([v, text]) => (
          <option key={v} value={v}>
            {text}
          </option>
        ))}
      </select>
    </label>
  );
}

function DefectView({
  projectId,
  findingId,
}: {
  readonly projectId: string;
  readonly findingId: string;
}) {
  const [loaded, reload] = useLoad(`defect:${projectId}:${findingId}`, () =>
    apiGet(`/projects/${projectId}/defects/${findingId}`, defectDetailSchema),
  );
  const [doc, setDoc] = useState<DefectDetail | null>(null);
  const back = () => {
    navigate('#defects', 'push');
  };
  if (loaded.state === 'loading') return <LoadingBlock />;
  if (loaded.state === 'error' || loaded.state === 'forbidden')
    return <Problem text="הליקוי לא נמצא או שאין לך גישה אליו." onRetry={back} />;
  const detail = doc ?? loaded.data;
  return (
    <DefectDetailView
      detail={detail}
      projectId={projectId}
      onBack={back}
      onChanged={(next) => {
        setDoc(next);
      }}
      onStale={() => {
        setDoc(null);
        reload();
      }}
    />
  );
}

function DefectDetailView({
  detail,
  projectId,
  onBack,
  onChanged,
  onStale,
}: {
  readonly detail: DefectDetail;
  readonly projectId: string;
  readonly onBack: () => void;
  readonly onChanged: (next: DefectDetail) => void;
  readonly onStale: () => void;
}) {
  const { defect, actions } = detail;
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [list] = useLoad(`defects-assignable:${projectId}`, () =>
    apiGet(`/projects/${projectId}/defects`, defectListResponseSchema),
  );
  const base = `/projects/${projectId}/defects/${defect.id}`;
  const run = (path: string, body: Record<string, unknown>, done: string) => {
    setBusy(true);
    setMessage(null);
    apiPost(`${base}/${path}`, { rowVersion: defect.rowVersion, ...body }, defectDetailSchema)
      .then((next) => {
        onChanged(next);
        setMessage({ kind: 'success', text: done });
        setAssigning(false);
      })
      .catch((error: unknown) => {
        setMessage({ kind: 'error', text: explain(error) });
        if (error instanceof ApiError && error.status === 409) onStale();
      })
      .finally(() => {
        setBusy(false);
      });
  };
  return (
    <article className="defect-detail" aria-labelledby="defect-title">
      <button type="button" className="button button--ghost back-button" onClick={onBack}>
        → כל הליקויים
      </button>
      <header className="defect-detail__head">
        <p className="hint">
          {defect.building} · דירה {defect.apartment} · {defect.protocolNumber} · נבדק{' '}
          {formatDate(defect.inspectedOn)}
        </p>
        <h2 id="defect-title">
          {defect.area} · {defect.component}
        </h2>
        <p className="defect-detail__text">{defect.description}</p>
        <p className="defect-detail__meta">
          <DefectStatus defect={defect} />
          {defect.workCategory !== null && (
            <span className="defect-chip">{defect.workCategory}</span>
          )}
          <span className="defect-chip">
            <Icon name="user" /> {defect.contractor?.name ?? 'ללא קבלן'}
          </span>
          {defect.dueOn !== null && (
            <span className={`defect-chip${defect.overdue ? ' defect-chip--late' : ''}`}>
              {defect.overdue ? 'באיחור · ' : 'יעד '}
              {formatDate(defect.dueOn)}
            </span>
          )}
        </p>
      </header>
      {message !== null && (
        <p
          className={`notice notice--${message.kind}`}
          role={message.kind === 'error' ? 'alert' : 'status'}
        >
          {message.text}
        </p>
      )}
      {(actions.approve || actions.reject) && (
        <section className="defect-next" aria-label="הפעולה הבאה">
          <p>
            <b>הקבלן דיווח שהתיקון בוצע.</b> בדקו את התמונות למטה ואשרו, או החזירו לטיפול עם הסבר.
          </p>
          <div className="form-actions">
            <button
              type="button"
              className="button button--primary"
              disabled={busy}
              onClick={() => {
                void askConfirm({
                  title: 'לאשר את התיקון ולסגור את הליקוי?',
                  message: 'הליקוי יסומן "אושר ונסגר". הפעולה נרשמת בהיסטוריה.',
                  confirmLabel: 'אישור וסגירה',
                }).then((yes) => {
                  if (yes) run('approve', {}, 'התיקון אושר והליקוי נסגר.');
                });
              }}
            >
              <Icon name="check" /> אישור וסגירה
            </button>
            <button
              type="button"
              className="button button--danger-quiet"
              disabled={busy}
              onClick={() => {
                void askText({
                  title: 'החזרה לטיפול',
                  message: 'מה עדיין לא תקין? ההסבר יוצג לקבלן.',
                  label: 'הסבר',
                  confirmLabel: 'החזרה לטיפול',
                  maxLength: 1000,
                }).then((reason) => {
                  if (reason === null) return;
                  if (reason.trim().length < 3) {
                    setMessage({ kind: 'error', text: 'כתבו בקצרה מה עדיין לא תקין.' });
                    return;
                  }
                  run('reject', { reason: reason.trim() }, 'הליקוי הוחזר לטיפול הקבלן.');
                });
              }}
            >
              החזרה לטיפול
            </button>
          </div>
        </section>
      )}
      {actions.release && (
        <section className="defect-next defect-release" aria-labelledby="defect-release">
          <h3 id="defect-release">שחרור לטיפול קבלן</h3>
          <p>
            הליקוי עדיין פנימי: הקבלן לא רואה אותו. בחרו קבלן (ומועד יעד אם רוצים) ושחררו לטיפול.
          </p>
          <AssignForm
            current={defect.contractor?.id ?? ''}
            dueOn={defect.dueOn ?? ''}
            options={list.state === 'ready' ? list.data.assignable : []}
            busy={busy}
            submitLabel="שחרור לקבלן"
            onSave={(contractorId, dueOn, note) => {
              run(
                'release',
                { contractorId, dueOn: dueOn === '' ? null : dueOn, note },
                'הליקוי שוחרר לטיפול הקבלן.',
              );
            }}
          />
        </section>
      )}
      <section aria-labelledby="defect-photos">
        <h3 id="defect-photos">תמונות מהמסירה</h3>
        <PhotoStrip photos={detail.photos} label="תמונות מהמסירה" />
      </section>
      <section aria-labelledby="defect-history">
        <h3 id="defect-history">היסטוריית הטיפול</h3>
        <RepairHistory history={detail.history} />
      </section>
      {actions.assign && (
        <section aria-labelledby="defect-assign">
          <h3 id="defect-assign">קבלן אחראי ומועד יעד</h3>
          {assigning ? (
            <AssignForm
              current={defect.contractor?.id ?? ''}
              dueOn={defect.dueOn ?? ''}
              options={list.state === 'ready' ? list.data.assignable : []}
              busy={busy}
              onCancel={() => {
                setAssigning(false);
              }}
              onSave={(contractorId, dueOn, note) => {
                run(
                  'assign',
                  { contractorId, dueOn: dueOn === '' ? null : dueOn, note },
                  'השיוך נשמר.',
                );
              }}
            />
          ) : (
            <div className="form-actions">
              <button
                type="button"
                className="button"
                onClick={() => {
                  setAssigning(true);
                }}
              >
                <Icon name="pencil" /> שינוי קבלן או מועד יעד
              </button>
              {actions.withdraw && (
                <button
                  type="button"
                  className="button button--danger-quiet"
                  disabled={busy}
                  onClick={() => {
                    void askText({
                      title: 'ביטול השחרור לקבלן',
                      message:
                        'הקבלן לא יראה עוד את הליקוי. ההיסטוריה והתמונות נשמרות. למה מבטלים?',
                      label: 'סיבה',
                      confirmLabel: 'ביטול השחרור',
                      maxLength: 1000,
                    }).then((reason) => {
                      if (reason === null) return;
                      if (reason.trim().length < 3) {
                        setMessage({ kind: 'error', text: 'כתבו בקצרה למה מבטלים את השחרור.' });
                        return;
                      }
                      run(
                        'withdraw',
                        { reason: reason.trim() },
                        'השחרור בוטל. הקבלן לא רואה עוד את הליקוי.',
                      );
                    });
                  }}
                >
                  ביטול השחרור
                </button>
              )}
            </div>
          )}
        </section>
      )}
    </article>
  );
}

function AssignForm({
  current,
  dueOn,
  options,
  busy,
  onCancel,
  onSave,
  submitLabel = 'שמירה',
}: {
  readonly current: string;
  readonly dueOn: string;
  readonly options: readonly { id: string; name: string }[];
  readonly busy: boolean;
  readonly onCancel?: () => void;
  readonly submitLabel?: string;
  readonly onSave: (contractorId: string, dueOn: string, note: string) => void;
}) {
  const [contractor, setContractor] = useState(current);
  const [due, setDue] = useState(dueOn);
  const [note, setNote] = useState('');
  return (
    <form
      className="defect-assign"
      onSubmit={(e) => {
        e.preventDefault();
        if (contractor !== '') onSave(contractor, due, note);
      }}
    >
      <label className="field">
        <span>קבלן אחראי</span>
        <select
          value={contractor}
          onChange={(e) => {
            setContractor(e.target.value);
          }}
          required
        >
          <option value="">בחירת קבלן</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>מועד יעד (רשות)</span>
        <input
          type="date"
          value={due}
          onChange={(e) => {
            setDue(e.target.value);
          }}
        />
      </label>
      <label className="field">
        <span>הערה (רשות)</span>
        <input
          value={note}
          maxLength={1000}
          onChange={(e) => {
            setNote(e.target.value);
          }}
        />
      </label>
      <div className="form-actions">
        <button
          type="submit"
          className="button button--primary"
          disabled={busy || contractor === ''}
        >
          {submitLabel}
        </button>
        {onCancel !== undefined && (
          <button type="button" className="button button--ghost" onClick={onCancel}>
            ביטול
          </button>
        )}
      </div>
    </form>
  );
}
